import type { ContentBuilderFieldColor } from './descriptor';

/** Runtime shape for legacy `colors` props and paired `color` props (`font` = text/icon). */
export type FontBackgroundColors = {
  font?: ContentBuilderFieldColor;
  background?: ContentBuilderFieldColor;
};

/** @deprecated Prefer {@link FontBackgroundColors}. */
export type CloseIconFieldColors = FontBackgroundColors;

export type FontBackgroundColorsMapping = {
  /** Legacy persisted key holding `{ font, background }`. */
  legacyKey: string;
  textColorKey: string;
  backgroundColorKey: string;
  /** When set, reads/writes under `resolvedRoot[nestedKey]` (e.g. cross-selling `colors`). */
  nestedKey?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function scopeRecord(
  root: Record<string, unknown>,
  nestedKey?: string,
): Record<string, unknown> {
  if (!nestedKey) {
    return root;
  }
  const nested = root[nestedKey];
  return isRecord(nested) ? nested : {};
}

function readLegacyFontBackgroundColors(value: unknown): FontBackgroundColors | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const next: FontBackgroundColors = {};
  if ('font' in value) {
    next.font = value['font'] as ContentBuilderFieldColor;
  }
  if ('background' in value) {
    next.background = value['background'] as ContentBuilderFieldColor;
  }
  return next.font !== undefined || next.background !== undefined ? next : undefined;
}

function readSplitFontBackgroundColors(
  resolved: Record<string, unknown>,
  mapping: FontBackgroundColorsMapping,
): FontBackgroundColors | undefined {
  const next: FontBackgroundColors = {};
  if (mapping.textColorKey in resolved) {
    next.font = resolved[mapping.textColorKey] as ContentBuilderFieldColor;
  }
  if (mapping.backgroundColorKey in resolved) {
    next.background = resolved[mapping.backgroundColorKey] as ContentBuilderFieldColor;
  }
  return next.font !== undefined || next.background !== undefined ? next : undefined;
}

/**
 * Resolves a font/background pair from split CB2 `color` props or a legacy `colors` object.
 * When `sourceConfig` omits the split keys, legacy `{ font, background }` wins over resolver defaults.
 */
export function resolveFontBackgroundColors(
  resolvedRoot: Record<string, unknown>,
  sourceRoot: Record<string, unknown> | null | undefined,
  mapping: FontBackgroundColorsMapping,
): FontBackgroundColors | undefined {
  const resolved = scopeRecord(resolvedRoot, mapping.nestedKey);
  const source = scopeRecord(sourceRoot ?? {}, mapping.nestedKey);
  const usesSplitKeys =
    mapping.textColorKey in source || mapping.backgroundColorKey in source;

  if (usesSplitKeys) {
    return readSplitFontBackgroundColors(resolved, mapping);
  }

  const legacy = readLegacyFontBackgroundColors(resolved[mapping.legacyKey]);
  if (legacy) {
    return legacy;
  }

  return readSplitFontBackgroundColors(resolved, mapping);
}

export const CLOSE_ICON_FIELD_COLORS_MAPPING: FontBackgroundColorsMapping = {
  legacyKey: 'close_icon_field_colors',
  textColorKey: 'close_icon_color',
  backgroundColorKey: 'close_icon_background_color',
};

export const CLOSE_ICON_COLORS_MAPPING: FontBackgroundColorsMapping = {
  legacyKey: 'close_icon_colors',
  textColorKey: 'close_icon_color',
  backgroundColorKey: 'close_icon_background_color',
  nestedKey: 'colors',
};

export const ACCENT_FIELD_COLORS_MAPPING: FontBackgroundColorsMapping = {
  legacyKey: 'accent_color',
  textColorKey: 'accent_font_color',
  backgroundColorKey: 'accent_background_color',
};

export const CANCEL_BUTTON_COLORS_MAPPING: FontBackgroundColorsMapping = {
  legacyKey: 'cancel_color',
  textColorKey: 'cancel_font_color',
  backgroundColorKey: 'cancel_background_color',
};

export const VALIDATE_BUTTON_COLORS_MAPPING: FontBackgroundColorsMapping = {
  legacyKey: 'validate_color',
  textColorKey: 'validate_font_color',
  backgroundColorKey: 'validate_background_color',
};

/** Close icon on modals using `close_icon_field_colors` (splash, email alerts, upselling). */
export function resolveCloseIconFieldColors(
  resolved: Record<string, unknown>,
  sourceConfig?: Record<string, unknown> | null,
): FontBackgroundColors | undefined {
  return resolveFontBackgroundColors(resolved, sourceConfig, CLOSE_ICON_FIELD_COLORS_MAPPING);
}

/** Close icon on cross-selling / bogof-style nested `colors` configs. */
export function resolveCloseIconColors(
  resolved: Record<string, unknown>,
  sourceConfig?: Record<string, unknown> | null,
): FontBackgroundColors | undefined {
  return resolveFontBackgroundColors(resolved, sourceConfig, CLOSE_ICON_COLORS_MAPPING);
}

export function resolveAccentFieldColors(
  resolved: Record<string, unknown>,
  sourceConfig?: Record<string, unknown> | null,
): FontBackgroundColors | undefined {
  return resolveFontBackgroundColors(resolved, sourceConfig, ACCENT_FIELD_COLORS_MAPPING);
}

export function resolveCancelButtonColors(
  resolved: Record<string, unknown>,
  sourceConfig?: Record<string, unknown> | null,
): FontBackgroundColors | undefined {
  return resolveFontBackgroundColors(resolved, sourceConfig, CANCEL_BUTTON_COLORS_MAPPING);
}

export function resolveValidateButtonColors(
  resolved: Record<string, unknown>,
  sourceConfig?: Record<string, unknown> | null,
): FontBackgroundColors | undefined {
  return resolveFontBackgroundColors(resolved, sourceConfig, VALIDATE_BUTTON_COLORS_MAPPING);
}
